import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

/**
 * Named accent themes. Each maps to a `[data-theme=…]` block in src/index.css
 * that re-tunes the primary/accent/ring tokens (light + dark). `indigo` is the
 * default and matches the base :root / .dark values.
 */
export const ACCENTS = ['indigo', 'violet', 'emerald', 'rose', 'amber', 'sky'] as const;
export type Accent = (typeof ACCENTS)[number];

export const DEFAULT_ACCENT: Accent = 'indigo';

/** Human labels + a representative swatch class for each accent. */
export const ACCENT_META: Record<Accent, { label: string; swatch: string }> = {
  indigo: { label: 'Indigo', swatch: 'bg-indigo-500' },
  violet: { label: 'Violet', swatch: 'bg-violet-500' },
  emerald: { label: 'Émeraude', swatch: 'bg-emerald-500' },
  rose: { label: 'Rose', swatch: 'bg-rose-500' },
  amber: { label: 'Ambre', swatch: 'bg-amber-500' },
  sky: { label: 'Ciel', swatch: 'bg-sky-500' },
};

/** Coerce any persisted/legacy value into a known accent (default: indigo). */
function normalizeAccent(value: unknown): Accent {
  return (ACCENTS as readonly string[]).includes(value as string)
    ? (value as Accent)
    : DEFAULT_ACCENT;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** True when the OS/browser currently prefers a dark color scheme. */
function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_QUERY).matches
  );
}

/** Collapse the tri-state preference into the concrete theme to render. */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

/**
 * Apply the resolved theme + accent to <html>:
 *  - toggle the `.dark` class (Tailwind's class strategy — the E2E suite and
 *    all dark-mode utilities depend on this),
 *  - set `color-scheme` for native controls,
 *  - set `data-theme` to the accent name, which selects the matching
 *    `[data-theme=…]` accent block in src/index.css.
 */
function applyTheme(theme: Theme, accent: Accent = DEFAULT_ACCENT): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  root.setAttribute('data-theme', normalizeAccent(accent));
}

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Theme preference; 'system' follows prefers-color-scheme. */
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Named accent theme applied as `data-theme` on <html>. */
  accent: Accent;
  setAccent: (accent: Accent) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      theme: 'system',
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme, get().accent);
      },

      accent: DEFAULT_ACCENT,
      setAccent: (accent) => {
        const next = normalizeAccent(accent);
        set({ accent: next });
        applyTheme(get().theme, next);
      },
    }),
    {
      name: 'psn-ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        theme: s.theme,
        accent: s.accent,
      }),
      // Coerce any legacy accent value (older builds stored raw RGB channels
      // or null) into a known accent name on rehydrate.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<UIState>;
        return { ...current, ...p, accent: normalizeAccent(p.accent) };
      },
      // Apply the persisted theme + accent as soon as the store rehydrates.
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme, normalizeAccent(state.accent));
      },
    },
  ),
);

// Initialize on store load (covers the no-persisted-state case) and keep the
// 'system' preference in sync with the OS setting.
applyTheme(useUIStore.getState().theme, useUIStore.getState().accent);

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mql = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (useUIStore.getState().theme === 'system') {
      applyTheme('system', useUIStore.getState().accent);
    }
  };
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange);
  } else if (typeof mql.addListener === 'function') {
    // Safari < 14 fallback.
    mql.addListener(onChange);
  }
}
